import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
  ColDef,
  GridOptions,
  GridApi,
  SelectionChangedEvent,
  GridReadyEvent,
  ValueGetterParams,
  ITooltipParams
} from 'ag-grid-community';
import { GridCellComponent } from '@root/search/components/grid/grid-cell/grid-cell.component';
import {
  GridViewState,
  PropertyInformation,
  SearchResultData,
  SidePanelTab,
  SidePanelView
} from '@store/search-board/search-board.model';
import { FormatterService } from '@shared/services/formatter.service';
import { SearchHeaderCellComponent } from '@root/search/components/grid/search-header-cell/search-header-cell.component';
import { StringUtils } from '@shared/utils/string-utils';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';
import { selectSidePanelState } from '@store/search-board/search-board.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'rlst-property-suggestion',
  templateUrl: './property-suggestion.component.html',
  styleUrls: ['./property-suggestion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PropertySuggestionComponent implements OnInit {
  public rowData: Array<PropertyInformation>;
  public columnDefs: Array<ColDef>;
  public defaultColDef: ColDef;
  public gridOptions: GridOptions;
  public isProceedDisabled: boolean = true;
  private gridApi: GridApi;

  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);
  private autoSizeColumns: string[] = ['APN/Tax ID', 'Address', 'City'];

  constructor(private readonly dialogRef: MatDialogRef<PropertySuggestionComponent>,
              @Inject(MAT_DIALOG_DATA) public data: {results: SearchResultData, isMobile: boolean},
              private readonly formatterService: FormatterService,
              private readonly store$: Store<AppState>,
              private readonly mixpanelService: MixpanelService) { }

  ngOnInit(): void {
    this.defaultColDef = {
      resizable: false,
      suppressSizeToFit: true,
      lockPosition: true,
      headerComponent: SearchHeaderCellComponent,
      cellRenderer: GridCellComponent
    };
    this.gridOptions = {
      theme: 'legacy',
      headerHeight: 40,
      rowHeight: 32,
      suppressMenuHide: true,
      enableBrowserTooltips: false,
      rowSelection: {
        mode: 'multiRow',
        headerCheckbox: true,
        checkboxes: true
      }
    };
    this.rowData = this.data.results.propertySummaryList.map((item: PropertyInformation) => {
      return {...item, isChecked: false };
    });
    this.columnDefs = [
      {
        headerName: '',
        field: 'checkbox',
        width: 40,
        cellClass: 'custom-select-all',
        sortable: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        lockPosition: 'left',
        pinned: 'left'
      },
      {
        headerName: 'APN/Tax ID',
        width: 118,
        sortable: true,
        tooltipValueGetter: (params: ITooltipParams) => params.data.propertyData.APN,
        valueGetter: (params: ValueGetterParams) => params.data.propertyData.APN
      },
      {
        headerName: 'Owner Name',
        width: 160,
        sortable: true,
        tooltipValueGetter: (params: ITooltipParams) => StringUtils.formatCamelCase(params.data.propertyData.OWNER_NAME_1),
        valueGetter: (params: ValueGetterParams) => StringUtils.formatCamelCase(params.data.propertyData.OWNER_NAME_1)
      },
      {
        headerName: 'Address',
        width: 180,
        sortable: true,
        tooltipValueGetter: (params: ITooltipParams) => this.formatterService.getPropertyAddress(params.data.propertyData),
        valueGetter: (params: ValueGetterParams) => this.formatterService.getPropertyAddress(params.data.propertyData)
      },
      {
        headerName: 'City',
        width: 150,
        sortable: true,
        wrapText: false,
        autoHeight: false,
        tooltipValueGetter: (params: ITooltipParams) => StringUtils.formatCamelCase(params.data.propertyData.PROPERTY_CITY_NAME),
        valueGetter: (params: ValueGetterParams) => StringUtils.formatCamelCase(params.data.propertyData.PROPERTY_CITY_NAME)
      },
      {
        headerName: 'County, State',
        width: 138,
        sortable: true,
        tooltipValueGetter: (params: ITooltipParams) => this.formatterService.formatCountyState(params.data.propertyData),
        valueGetter: (params: ValueGetterParams) => this.formatterService.formatCountyState(params.data.propertyData)
      }
    ];

    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    const columnIds: string[] = params.api.getAllDisplayedColumns()
      .filter((col: any) => this.autoSizeColumns.includes(col.getUserProvidedColDef().headerName))
      .map((col: any) => col.getId());

    params.api.autoSizeColumns(columnIds);
  }

  public onSelectionChanged(params: SelectionChangedEvent): void {
    this.isProceedDisabled = !params.api.getSelectedNodes().length;
  }

  public close(): void {
    this.logMixPanelTracking('canceled', 0);
    this.dialogRef.close();
  }

  public proceed(): void {
    const result = this.data.isMobile ? this.getCheckedRows() : this.gridApi.getSelectedRows();
    const propertyIdentifiers = result.map((propertyInformation) => propertyInformation.propertyIdentifier);
    this.logMixPanelTracking('proceed', propertyIdentifiers.length);
    this.dialogRef.close({propertyIdentifiers});
  }

  public toggleAllRows(selected: boolean): void {
    this.rowData.forEach((row: PropertyInformation) => row.isChecked = selected);
  }

  public toggleRow(row: PropertyInformation): void {
    row.isChecked = !row.isChecked;
  }

  public getCheckedRows(): Array<PropertyInformation> {
    return this.rowData.filter((row: PropertyInformation) => row.isChecked);
  }

  public isAllRowsSelected(): boolean {
    return this.getCheckedRows().length === this.rowData.length;
  }

  private logMixPanelTracking(actionType: string, numberSelected: number) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track('Search - Property Suggestion Click', {
      currentPageName: this.searchMixpanelUtil.currentPageName,
      searchType: this.sidePanelTabOpened,
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      countyList: this.searchMixpanelUtil.countyList,
      resultDisplay: this.gridViewState(this.searchMixpanelUtil.gridViewState),
      numberOfResults: this.data.results.propertySummaryList.length,
      numberOfResultsSelected: numberSelected,
      continueSelection: actionType
    });
  }

  private gridViewState(gridViewState: GridViewState): string {
    if (gridViewState.list > 0 && gridViewState.map > 0) {
      return 'spit';
    } else if (gridViewState.list === 100) {
      return 'list';
    } else if (gridViewState.map === 100) {
      return 'map';
    }
    return '';
  }
}
